import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Search, Calculator, Package, Edit2, PackagePlus } from 'lucide-react';
import api, { fmtIDR } from '@/lib/api';
import EmptyState from '@/components/common/EmptyState';
import { toast } from 'sonner';
import { DeleteProductButton } from '@/components/products/ProductsManager';

export default function OwnerProducts() {
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [editingHpp, setEditingHpp] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get('/products?active_only=false');
      setProducts(data);
    } catch (e) { console.error('[OwnerProducts] gagal load produk:', e); }
  };
  useEffect(() => { load(); }, []);

  const highlight = params.get('highlight');
  useEffect(() => {
    if (highlight && products.length > 0) {
      const p = products.find((x) => x.id === highlight);
      if (p) {
        setEditingHpp(p);
        setParams({});
      }
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, products]);

  const filtered = useMemo(() => products.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
  }), [products, query]);

  return (
    <AppShell nav={ownerNav} title="Barang & HPP" showBell>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="owner-products-search" placeholder="Cari SKU / nama / kategori" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" />
          </div>
          <div className="text-sm text-muted-foreground">{filtered.length} barang</div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon={Package} title="Belum ada barang" description="Admin dapat menambahkan barang dari panel Admin." />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="owner-products-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Nama</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-right">Harga Jual</TableHead>
                      <TableHead className="text-right">HPP</TableHead>
                      <TableHead className="text-right">Stok</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((p) => {
                      const low = p.stock <= (p.low_stock_threshold || 5);
                      return (
                        <TableRow key={p.id} data-testid={`owner-product-row-${p.id}`}>
                          <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {p.photo_url ? <img src={p.photo_url} alt={p.name} className="h-9 w-9 rounded object-cover" /> : <div className="h-9 w-9 rounded bg-muted flex items-center justify-center"><Package className="h-4 w-4 text-muted-foreground" /></div>}
                              <span className="font-medium">{p.name}</span>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="secondary">{p.category}</Badge></TableCell>
                          <TableCell className="text-right tabular-nums">{fmtIDR(p.selling_price)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {p.total_hpp ? fmtIDR(p.total_hpp) : <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Belum diisi</Badge>}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums ${low ? 'text-rose-600 font-semibold' : ''}`}>{p.stock}</TableCell>
                          <TableCell>{p.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Aktif</Badge> : <Badge variant="secondary">Nonaktif</Badge>}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="sm" variant="outline" onClick={() => setEditingProduct(p)} data-testid={`owner-edit-product-${p.id}`}>
                              <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingHpp(p)} data-testid={`owner-hpp-edit-${p.id}`}>
                              <Calculator className="h-3.5 w-3.5 mr-1" /> HPP
                            </Button>
                            <DeleteProductButton product={p} onDeleted={load} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <EditProductDialog product={editingProduct} onClose={() => setEditingProduct(null)} onSaved={() => { load(); setEditingProduct(null); }} />
      <HPPDialog editing={editingHpp} onClose={() => setEditingHpp(null)} onSaved={() => { load(); setEditingHpp(null); }} />
    </AppShell>
  );
}

function EditProductDialog({ product, onClose, onSaved }) {
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [threshold, setThreshold] = useState(5);
  const [active, setActive] = useState(true);
  const [stock, setStock] = useState(0);
  const [stockReason, setStockReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setSku(product.sku || '');
      setName(product.name || '');
      setCategory(product.category || '');
      setPhotoUrl(product.photo_url || '');
      setSellingPrice(product.selling_price || 0);
      setThreshold(product.low_stock_threshold || 5);
      setActive(product.active);
      setStock(product.stock || 0);
      setStockReason('');
    }
  }, [product]);

  const save = async () => {
    if (!product) return;
    if (!sku || !name || !category || !sellingPrice) return toast.error('SKU, Nama, Kategori, dan Harga Jual wajib diisi');
    setSaving(true);
    try {
      // 1) Patch product basic fields
      const updates = {};
      if (sku !== product.sku) updates.sku = sku;
      if (name !== product.name) updates.name = name;
      if (category !== product.category) updates.category = category;
      if ((photoUrl || null) !== (product.photo_url || null)) updates.photo_url = photoUrl || null;
      if (Number(sellingPrice) !== product.selling_price) updates.selling_price = Number(sellingPrice);
      if (Number(threshold) !== product.low_stock_threshold) updates.low_stock_threshold = Number(threshold);
      if (active !== product.active) updates.active = active;
      if (Object.keys(updates).length > 0) {
        await api.patch(`/products/${product.id}`, updates);
      }
      // 2) Adjust stock if changed
      if (Number(stock) !== product.stock) {
        await api.post(`/products/${product.id}/stock-adjust`, {
          new_stock: Number(stock),
          reason: stockReason || 'Owner update',
        });
      }
      toast.success('Barang diperbarui');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="owner-edit-product-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Barang</DialogTitle>
          <div className="text-sm text-muted-foreground">Owner dapat mengubah semua field termasuk SKU dan Stok.</div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>SKU <span className="text-rose-600">*</span></Label>
              <Input value={sku} onChange={(e) => setSku(e.target.value)} data-testid="owner-edit-sku" />
            </div>
            <div className="space-y-1.5">
              <Label>Kategori <span className="text-rose-600">*</span></Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} data-testid="owner-edit-category" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Nama Barang <span className="text-rose-600">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="owner-edit-name" />
          </div>
          <div className="space-y-1.5">
            <Label>URL Foto</Label>
            <Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://..." data-testid="owner-edit-photo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Harga Jual (Rp) <span className="text-rose-600">*</span></Label>
              <Input type="number" min="0" step="1000" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} data-testid="owner-edit-price" />
            </div>
            <div className="space-y-1.5">
              <Label>Low Stock Threshold</Label>
              <Input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="owner-edit-threshold" />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/40">
            <div>
              <Label className="text-sm">Status Aktif</Label>
              <div className="text-xs text-muted-foreground">Barang nonaktif tidak muncul di POS Kasir.</div>
            </div>
            <Switch checked={active} onCheckedChange={setActive} data-testid="owner-edit-active" />
          </div>

          <div className="rounded-lg border-2 border-primary/20 bg-accent/40 p-3 space-y-3">
            <div className="flex items-center gap-2">
              <PackagePlus className="h-4 w-4 text-primary" />
              <Label className="text-sm font-medium">Sesuaikan Stok</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Stok Sekarang</Label>
                <div className="h-10 rounded-md border bg-muted flex items-center px-3 tabular-nums font-semibold" data-testid="owner-edit-current-stock">{product?.stock || 0}</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Stok Baru</Label>
                <Input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} data-testid="owner-edit-new-stock" />
              </div>
            </div>
            {Number(stock) !== product?.stock && (
              <div className="space-y-1.5">
                <Label className="text-xs">Alasan Penyesuaian (audit)</Label>
                <Input value={stockReason} onChange={(e) => setStockReason(e.target.value)} placeholder="Contoh: koreksi stock opname, retur, dsb." data-testid="owner-edit-stock-reason" />
              </div>
            )}
            <div className="text-xs text-muted-foreground">Perubahan stok akan tercatat sebagai mutasi (stock movement) dengan audit trail Owner.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="owner-edit-save-button">{saving ? 'Menyimpan...' : 'Simpan Perubahan'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HPPDialog({ editing, onClose, onSaved }) {
  const [hargaPabrik, setHargaPabrik] = useState('');
  const [nonPPN, setNonPPN] = useState(false);
  const [handling, setHandling] = useState('');
  const [mobilisasi, setMobilisasi] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      const h = editing.hpp_components;
      setHargaPabrik(h?.harga_pabrik || '');
      setNonPPN(h?.ppn_mode === 'Non-PPN');
      setHandling(h?.ongkos_handling || '');
      setMobilisasi(h?.ongkos_mobilisasi || '');
    }
  }, [editing]);

  const hp = Number(hargaPabrik) || 0;
  const hd = Number(handling) || 0;
  const mb = Number(mobilisasi) || 0;
  const withTax = nonPPN ? hp * 1.11 : hp;
  const totalHpp = Math.round((withTax + hd + mb) * 100) / 100;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/products/${editing.id}/hpp`, {
        harga_pabrik: hp,
        ppn_mode: nonPPN ? 'Non-PPN' : 'PPN',
        ongkos_handling: hd,
        ongkos_mobilisasi: mb,
      });
      toast.success('HPP berhasil disimpan');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan HPP');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="owner-hpp-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Kalkulator HPP</DialogTitle>
          <div className="text-sm text-muted-foreground">{editing?.name} • {editing?.sku}</div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Harga Pabrik (Rp)</Label>
            <Input data-testid="hpp-harga-pabrik" type="number" min="0" step="1000" value={hargaPabrik} onChange={(e) => setHargaPabrik(e.target.value)} />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/40">
            <div>
              <Label className="text-sm">Non-PPN (+11%)</Label>
              <div className="text-xs text-muted-foreground">Jika belum termasuk PPN, sistem akan menambahkan 11% otomatis.</div>
            </div>
            <Switch data-testid="owner-hpp-vat-switch" checked={nonPPN} onCheckedChange={setNonPPN} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Ongkos Handling</Label>
              <Input data-testid="hpp-handling" type="number" min="0" step="1000" value={handling} onChange={(e) => setHandling(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Mobilisasi (Pabrik-Gudang)</Label>
              <Input data-testid="hpp-mobilisasi" type="number" min="0" step="1000" value={mobilisasi} onChange={(e) => setMobilisasi(e.target.value)} />
            </div>
          </div>
          <div className="rounded-xl border-2 border-primary/20 bg-accent/60 p-4">
            <div className="text-xs uppercase text-accent-foreground/80 tracking-wide">Total HPP</div>
            <div className="text-3xl font-heading font-semibold tabular-nums text-accent-foreground" data-testid="owner-hpp-total">{fmtIDR(totalHpp)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {nonPPN ? `${fmtIDR(hp)} + PPN 11% = ${fmtIDR(withTax)}` : fmtIDR(hp)} + Handling {fmtIDR(hd)} + Mobilisasi {fmtIDR(mb)}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving || hp <= 0} data-testid="owner-hpp-save-button">{saving ? 'Menyimpan...' : 'Simpan HPP'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
